import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import UpgradePlanModal from "@/components/UpgradePlanModal";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Search, Filter, FolderOpen, MapPin, Zap, Calendar, ChevronRight, MoreHorizontal, Archive, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import EditProjectDialog from "@/components/EditProjectDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";
import { can } from "@/lib/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RAGDot = ({ status }) => {
  const colors = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
  return <span className={`w-2 h-2 rounded-full inline-block ${colors[status] || colors.green}`} />;
};

const PHASE_NAMES = ["", "Lead & Contract", "Engineering & Design", "Statutory Approvals", "Procurement", "Civil Works", "Installation", "Testing & Commissioning", "Handover"];

export default function ProjectListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [upgradeLimitMsg, setUpgradeLimitMsg] = useState(null);
  const [codWarning, setCodWarning] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [form, setForm] = useState({
    name: "", client_name: "", client_email: "", location: "",
    capacity_kwp: "", contract_value: "", expected_cod: "", description: "",
    project_type: "rooftop"
  });

  const MIN_DAYS = { rooftop: 60, ground_mount: 90, ground_mount_oa: 180, blank: 0 };
  const TYPE_LABELS = { rooftop: "Rooftop", ground_mount: "Ground Mount", ground_mount_oa: "Ground Mount (Open Access)", blank: "Blank" };

  const canCreate = can(user, "projects.edit");
  const canArchive = can(user, "projects.delete");   // archive / restore / permanent delete are admin-only (backend enforces)

  useEffect(() => { fetchProjects(); }, []);

  const checkCodWarning = (cod, type) => {
    if (!cod || !type) { setCodWarning(""); return; }
    const days = Math.ceil((new Date(cod) - new Date()) / 86400000);
    const minDays = MIN_DAYS[type] || 60;
    if (days < minDays) {
      setCodWarning(`Warning: ${days} days is too tight for a ${TYPE_LABELS[type]} project. Recommended minimum: ${minDays} days. Consider a later COD for a realistic timeline.`);
    } else if (days < minDays * 1.2) {
      setCodWarning(`Note: ${days} days is feasible but tight for a ${TYPE_LABELS[type]} project. Ensure all teams are aligned.`);
    } else {
      setCodWarning("");
    }
  };

  const fetchProjects = async (inclArchived = false) => {
    try {
      const url = inclArchived ? `${API}/projects?include_archived=true` : `${API}/projects`;
      const { data } = await axios.get(url);
      setProjects(inclArchived ? data : data.filter(p => !p.is_archived));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const createProject = async (e) => {
    e.preventDefault();
    if (!form.name || !form.client_name || !form.location || !form.capacity_kwp) {
      toast.error("Please fill all required fields");
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/projects`, {
        ...form,
        capacity_kwp: parseFloat(form.capacity_kwp),
        contract_value: parseFloat(form.contract_value) || 0,
      });
      const taskCount = { rooftop: 59, ground_mount: 66, ground_mount_oa: 87, blank: 0 }[form.project_type] ?? 0;
      toast.success(taskCount > 0 ? `Project created with ${taskCount} tasks auto-generated!` : "Blank project created! Add custom tasks to get started.");
      setShowCreate(false);
      setForm({ name: "", client_name: "", client_email: "", location: "", capacity_kwp: "", contract_value: "", expected_cod: "", description: "", project_type: "rooftop" });
      navigate(`/app/projects/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 403 && detail.toLowerCase().includes("limit")) {
        setShowCreate(false);
        setUpgradeLimitMsg(detail);
      } else {
        toast.error(detail || "Failed to create project");
      }
    } finally {
      setCreating(false);
    }
  };

  const archiveProject = async (id) => {
    const proj = projects.find(p => p.id === id);
    const isArchived = proj?.is_archived;
    try {
      if (isArchived) {
        await axios.put(`${API}/projects/${id}`, { is_archived: false });
        setProjects(p => p.map(pr => pr.id === id ? { ...pr, is_archived: false } : pr));
        toast.success("Project unarchived");
      } else {
        await axios.delete(`${API}/projects/${id}`);
        setProjects(p => p.filter(pr => pr.id !== id));
        toast.success("Project archived");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update the project"); }
  };

  const permanentDeleteProject = async (id, name) => {
    if (!window.confirm(`PERMANENTLY DELETE "${name}"? This cannot be undone. All tasks, documents, and financials will be deleted.`)) return;
    if (!window.confirm(`Last warning: Delete "${name}" forever?`)) return;
    try {
      await axios.delete(`${API}/projects/${id}/permanent`);
      setProjects(p => p.filter(pr => pr.id !== id));
      toast.success("Project permanently deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete project");
    }
  };


  const allFiltered = projects.filter(p =>
    (p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.client_name.toLowerCase().includes(search.toLowerCase()) ||
    p.location.toLowerCase().includes(search.toLowerCase()))
  );
  const filtered = allFiltered.filter(p => {
    if (statusFilter === "active") return p.status !== "completed" && !p.is_archived;
    if (statusFilter === "completed") return p.status === "completed" && !p.is_archived;
    if (statusFilter === "archived") return p.is_archived === true;
    return !p.is_archived;
  });

  const activeCnt = allFiltered.filter(p => p.status !== "completed" && !p.is_archived).length;
  const completedCnt = allFiltered.filter(p => p.status === "completed" && !p.is_archived).length;
  const archivedCnt = allFiltered.filter(p => p.is_archived).length;

  const handleStatusFilterChange = (k) => {
    setStatusFilter(k);
    if (k === "archived") {
      fetchProjects(true);
    } else if (statusFilter === "archived") {
      fetchProjects(false);
    }
  };

  const formatValue = (n) => {
    if (!n) return "—";
    if (n >= 10000000) return `₹${(n/10000000).toFixed(1)} Cr`;
    if (n >= 100000) return `₹${(n/100000).toFixed(1)} L`;
    return `₹${n.toLocaleString("en-IN")}`;
  };

  return (
    <div className="p-6 animate-fade-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#1B2B22]">Projects</h1>
          <p className="text-sm text-[#64748B]">{activeCnt} active · {completedCnt} completed</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Status filter tabs */}
          <div className="flex bg-[#F1F5F9] rounded-lg p-1 gap-1">
            {[["active","Active"],["completed","Completed"],["archived","Archived"],["all","All"]].map(([k,l]) => (
              <button key={k} onClick={() => handleStatusFilterChange(k)} data-testid={`filter-${k}`}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${statusFilter===k?"bg-[rgba(47,125,82,0.1)] text-[#2F7D52] shadow":"text-[#8AA496] hover:text-[#1B2B22]"}`}>
                {l}
              </button>
            ))}
          </div>
          {canCreate && (
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 bg-[#2F7D52] text-white font-semibold px-4 py-2 rounded text-sm hover:bg-[#266B44] transition-colors active:scale-[0.98]"
              data-testid="create-project-btn"
            >
              <Plus size={14} />
              New Project
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-3 top-2.5 text-[#94A3B8]" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search projects, clients, locations..."
          className="w-full border border-[rgba(46,100,68,0.15)] rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white text-[#1B2B22] max-w-md"
          data-testid="project-search"
        />
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white border border-[rgba(46,100,68,0.1)] rounded-2xl p-5 h-48 animate-pulse">
              <div className="h-4 bg-slate-100 rounded w-3/4 mb-3" />
              <div className="h-3 bg-slate-100 rounded w-1/2 mb-6" />
              <div className="h-2 bg-slate-100 rounded w-full" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 bg-[#FEF3C7] rounded-full flex items-center justify-center mb-4">
            <FolderOpen size={24} className="text-[#2F7D52]" />
          </div>
          <h3 className="font-heading font-semibold text-lg text-[#1B2B22] mb-2">
            {search ? "No projects match your search" : statusFilter === "completed" ? "No completed projects yet" : statusFilter === "archived" ? "No archived projects" : statusFilter === "active" ? "No active projects" : "No projects yet"}
          </h3>
          <p className="text-sm text-[#64748B] mb-4">
            {search ? "Try a different search term" : statusFilter === "completed" ? "Projects will appear here once 100% of tasks are completed" : statusFilter === "archived" ? "Archive a project from its 3-dot menu to see it here" : "Create your first solar EPC project and auto-generate up to 87 tasks based on project type"}
          </p>
          {canCreate && !search && statusFilter !== "completed" && statusFilter !== "archived" && (
            <button
              onClick={() => setShowCreate(true)}
              className="bg-[#2F7D52] text-white font-semibold px-5 py-2.5 rounded text-sm hover:bg-[#266B44] transition-colors"
            >
              Create First Project
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p, i) => (
            <div
              key={p.id}
              className="bg-white border border-[rgba(46,100,68,0.1)] rounded-2xl p-5 hover:border-[rgba(47,125,82,0.3)] hover:shadow-lg transition-all duration-150 group animate-fade-up"
              style={{ animationDelay: `${i * 50}ms` }}
              data-testid={`project-card-${p.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <Link to={`/app/projects/${p.id}`} className="font-heading font-semibold text-sm text-[#1B2B22] hover:text-[#2F7D52] transition-colors block truncate">
                    {p.name}
                  </Link>
                  <div className="text-xs text-[#64748B] mt-0.5">{p.client_name}</div>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <div className="flex items-center gap-1.5">
                    <RAGDot status={p.rag_status} />
                    <span className="text-[10px] text-[#64748B]">{({ green: "On Track", amber: "At Risk", red: "Delayed" })[p.rag_status]}</span>
                  </div>
                  {canCreate && (
                    <DropdownMenu>
                      <DropdownMenuTrigger className="p-1 rounded hover:bg-slate-100 text-[#64748B] transition-all" data-testid={`project-menu-${p.id}`}>
                        <MoreHorizontal size={14} />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/app/projects/${p.id}`}>View Project</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditProject({...p}); setShowEdit(true); }}>
                          Edit Details
                        </DropdownMenuItem>
                        {!canArchive ? null : !p.is_archived ? (
                          <>
                            <DropdownMenuItem onClick={() => archiveProject(p.id)} className="text-amber-600">
                              <Archive size={12} className="mr-2" /> Archive
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => permanentDeleteProject(p.id, p.name)} className="text-red-700 font-semibold">
                              <Trash2 size={12} className="mr-2" /> Delete Permanently
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <>
                            <DropdownMenuItem onClick={() => archiveProject(p.id)} className="text-emerald-600">
                              Unarchive
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => permanentDeleteProject(p.id, p.name)} className="text-red-700 font-semibold">
                              <Trash2 size={12} className="mr-2" /> Delete Permanently
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mb-3">
                <div className="flex justify-between text-[10px] text-[#64748B] mb-1">
                  <span>Phase {p.current_phase}: {PHASE_NAMES[p.current_phase]}</span>
                  <span className="tabular-nums">{p.completion_percentage || 0}%</span>
                </div>
                <div className="h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-500"
                    style={{ width: `${p.completion_percentage || 0}%` }}
                  />
                </div>
              </div>

              {/* Meta */}
              <div className="flex flex-wrap gap-3 text-xs text-[#64748B]">
                <span className="flex items-center gap-1"><MapPin size={10} />{p.location}</span>
                <span className="flex items-center gap-1"><Zap size={10} />{p.capacity_kwp} kWp</span>
                {p.expected_cod && (
                  <span className="flex items-center gap-1"><Calendar size={10} />COD: {new Date(p.expected_cod).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                )}
              </div>

              {can(user, "projects.value.view") && p.contract_value > 0 && (
                <div className="mt-3 pt-3 border-t border-[#F1F5F9] text-xs text-[#64748B]">
                  Contract: <span className="font-medium text-[#1B2B22]">{formatValue(p.contract_value)}</span>
                </div>
              )}

              <Link
                to={`/app/projects/${p.id}`}
                className="mt-3 flex items-center justify-end gap-1 text-xs text-[#2F7D52] hover:text-[#2F7D52] font-medium"
              >
                Open Project <ChevronRight size={12} />
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Create Project Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-[#1B2B22]">New Solar Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={createProject} className="space-y-4 py-2">
            {/* Project Type */}
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Project Type *</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["rooftop",       "Rooftop",           "🏭"],
                  ["ground_mount",  "Ground Mount",      "🌱"],
                  ["ground_mount_oa","Ground Mount OA",  "⚡"],
                  ["blank",         "Blank",             "📋"],
                ].map(([val, lbl, icon]) => (
                  <button type="button" key={val}
                    onClick={() => { setForm(p => ({...p, project_type: val})); checkCodWarning(form.expected_cod, val); }}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 text-xs font-semibold transition-all ${form.project_type===val?"border-blue-500 bg-[rgba(47,125,82,0.08)] text-[#2F7D52]":"border-[rgba(46,100,68,0.15)] text-[#8AA496] hover:border-[rgba(47,125,82,0.5)]"}`}
                    data-testid={`project-type-${val}`}>
                    <span className="text-lg">{icon}</span>{lbl}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#94A3B8] mt-1">
                {form.project_type==="rooftop" && "59 tasks · Phases 1–8 + Miscellaneous"}
                {form.project_type==="ground_mount" && "66 tasks · Ground mount civil works included"}
                {form.project_type==="ground_mount_oa" && "87 tasks · Land, OA approvals, ABT meters, transformer"}
                {form.project_type==="blank" && "0 tasks · Start from scratch and add custom tasks"}
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Project Name *</label>
              <input type="text" required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Acme Foods - 500kWp Rooftop" className="w-full border border-[rgba(46,100,68,0.15)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white text-[#1B2B22]" data-testid="modal-project-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Client Name *</label>
                <input type="text" required value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} placeholder="Acme Foods Pvt Ltd" className="w-full border border-[rgba(46,100,68,0.15)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white text-[#1B2B22]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Client Email</label>
                <input type="email" value={form.client_email} onChange={e => setForm(p => ({ ...p, client_email: e.target.value }))} placeholder="ceo@acme.com" className="w-full border border-[rgba(46,100,68,0.15)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white text-[#1B2B22]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Location *</label>
                <input type="text" required value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Pune, Maharashtra" className="w-full border border-[rgba(46,100,68,0.15)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white text-[#1B2B22]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Capacity (kWp) *</label>
                <input type="number" required value={form.capacity_kwp} onChange={e => setForm(p => ({ ...p, capacity_kwp: e.target.value }))} placeholder="500" className="w-full border border-[rgba(46,100,68,0.15)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white text-[#1B2B22]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Contract Value (₹)</label>
                <input type="number" value={form.contract_value} onChange={e => setForm(p => ({ ...p, contract_value: e.target.value }))} placeholder="25000000" className="w-full border border-[rgba(46,100,68,0.15)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white text-[#1B2B22]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Expected COD</label>
                <input type="date" value={form.expected_cod}
                  onChange={e => { setForm(p => ({...p, expected_cod: e.target.value})); checkCodWarning(e.target.value, form.project_type); }}
                  className="w-full border border-[rgba(46,100,68,0.15)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white text-[#1B2B22]" />
              </div>
            </div>
            {/* COD Warning */}
            {codWarning && (
              <div className={`flex items-start gap-2 p-3 rounded-lg text-xs ${codWarning.startsWith("Warning") ? "bg-red-50 border border-red-200 text-red-700" : "bg-yellow-50 border border-yellow-200 text-yellow-700"}`}
                data-testid="cod-warning">
                <span className="text-sm mt-0.5">{codWarning.startsWith("Warning") ? "⚠️" : "💡"}</span>
                <span>{codWarning}</span>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Description</label>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Brief project description..." className="w-full border border-[rgba(46,100,68,0.15)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] resize-none bg-white text-[#1B2B22]" />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-[rgba(46,100,68,0.15)] rounded-lg text-sm text-[#8AA496] hover:bg-white/5 transition-colors">Cancel</button>
              <button type="submit" disabled={creating} className="bg-[#2F7D52] text-white font-semibold px-5 py-2 rounded text-sm hover:bg-[#266B44] transition-colors active:scale-[0.98] disabled:opacity-60" data-testid="create-project-submit">
                {creating ? "Creating..." : "Create Project"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <EditProjectDialog
        project={editProject}
        open={showEdit}
        onOpenChange={(o) => { setShowEdit(o); if (!o) setEditProject(null); }}
        onSaved={(data) => setProjects(p => p.map(pr => pr.id === data.id ? { ...pr, ...data } : pr))}
      />

      {upgradeLimitMsg && (
        <UpgradePlanModal message={upgradeLimitMsg} onClose={() => setUpgradeLimitMsg(null)} />
      )}
    </div>
  );
}
